import { useTranslation } from "../hooks/useTranslation";

function getWalletDebugSnapshot() {
    if (typeof window === "undefined") return [];
    const safeKeys = (value) => {
        try {
            return value && typeof value === "object" ? Object.keys(value).slice(0, 20).join(", ") : "";
        } catch (error) {
            return "unreadable";
        }
    };
    const providerFlags = (provider) => {
        if (!provider || typeof provider !== "object") return "missing";
        const flags = ["isMetaMask", "isTrust", "isTrustWallet", "isTokenPocket", "isCoinbaseWallet"]
            .filter((key) => Boolean(provider[key]))
            .join(", ");
        return flags || "no wallet flags";
    };
    const eth = window.ethereum;
    return [
        ["ua", navigator.userAgent || ""],
        ["href", window.location.href || ""],
        ["ethereum", eth ? `yes / request:${typeof eth.request} / on:${typeof eth.on} / ${providerFlags(eth)}` : "no"],
        ["ethereum.providers", Array.isArray(eth?.providers) ? String(eth.providers.length) : "no"],
        ["trustwallet", window.trustwallet ? `yes / keys:${safeKeys(window.trustwallet)}` : "no"],
        ["trustWallet", window.trustWallet ? `yes / keys:${safeKeys(window.trustWallet)}` : "no"],
        ["tokenpocket", window.tokenpocket ? `yes / keys:${safeKeys(window.tokenpocket)}` : "no"],
        ["intent", window.localStorage?.getItem("trustWalletBrowserIntentAt") || "none"],
    ];
}

export default function WalletRequiredCard({
    variant = "install",
    onConnect,
    isConnecting = false,
    metaMaskDownloadUrl = "https://metamask.io/download/",
    trustWalletDeepLinkUrl,
    rememberTrustWalletIntent,
    walletDebugEnabled = false,
    walletConnectAvailable = false,
    walletConnectProjectIdMissing = false,
    isTokenPocketBlocked = false,
}) {
    const { t } = useTranslation();
    const isInstall = variant === "install";
    const walletDebugSnapshot = walletDebugEnabled ? getWalletDebugSnapshot() : [];

    return (
        <div className="rounded-2xl bg-[#1a1023]/90 border border-[#9770DD]/30 p-6 sm:p-8 shadow-lg shadow-[#9770DD]/10 max-w-md mx-auto">
            <h2 className="text-xl sm:text-2xl font-semibold text-white mb-3">
                {isTokenPocketBlocked
                    ? t("header.tokenPocketNotSupportedTitle")
                    : isInstall
                        ? t("header.web3WalletRequired")
                        : t("header.pleaseConnectWallet")}
            </h2>
            <p className="text-white/80 text-sm sm:text-base mb-6">
                {isTokenPocketBlocked
                    ? t("header.tokenPocketNotSupportedDescription")
                    : isInstall
                        ? t("header.installWeb3WalletOrOpenDapp")
                        : t("header.connectWalletToContinue")}
            </p>
            {isTokenPocketBlocked ? (
                <div className="space-y-3">
                    <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                        {t("header.tokenPocketBlockedHint")}
                    </p>
                </div>
            ) : isInstall ? (
                <div className="space-y-3">
                    <a
                        href={trustWalletDeepLinkUrl || metaMaskDownloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={rememberTrustWalletIntent}
                        className="inline-flex items-center justify-center w-full py-3 px-6 rounded-xl bg-[#9770DD] hover:bg-[#7a1fd6] text-white font-semibold transition-colors"
                    >
                        {t("header.openInTrustWalletBrowser")}
                    </a>
                    <a
                        href={metaMaskDownloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-full py-3 px-6 rounded-xl border border-[#9770DD]/50 text-white/90 hover:bg-[#9770DD]/10 font-semibold transition-colors"
                    >
                        {t("header.installWeb3WalletButton")}
                    </a>
                    <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
                        {walletConnectProjectIdMissing
                            ? t("header.walletConnectProjectIdMissing")
                            : walletConnectAvailable
                                ? t("header.walletConnectFallbackHint")
                                : t("header.trustWalletRefreshHint")}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    <button
                        type="button"
                        onClick={onConnect}
                        disabled={isConnecting}
                        className="w-full py-3 px-6 rounded-xl bg-[#9770DD] hover:bg-[#7a1fd6] text-white font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {isConnecting ? t("common.loading") : t("header.connectWallet")}
                    </button>
                </div>
            )}
            {walletDebugEnabled ? (
                <div className="mt-4 rounded-xl border border-yellow-400/40 bg-gray-950/40 p-3 text-left">
                    <div className="text-yellow-200 text-xs font-semibold mb-2">Wallet Debug</div>
                    <div className="space-y-1 text-[11px] leading-relaxed text-white/70 break-words">
                        {walletDebugSnapshot.map(([label, value]) => (
                            <div key={label}><span className="text-white/90">{label}:</span> {String(value)}</div>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
